import router from '@/router';
import {
  getCachedBlocksPlain,
  makeExcerpt,
} from '@/utils/pageBlocksPlainText';

/**
 * Поиск по сайту — та же логика, что в AppSearch (Header).
 * CMS-страницы: title/meta + plain text блоков (глубокий поиск).
 * @param {string} query
 * @param {object} sources
 * @returns {Array<{ key: string, path: string|object, meta: { title: string, description: string } }>}
 */
export function performSiteSearch(query, sources = {}) {
  const {
    paidServices = [],
    publishedPages = [],
    news = [],
    employees = [],
    managements = [],
    chiefMedicalOfficer = [],
  } = sources;

  const lowerCaseQuery = String(query || '').toLowerCase().trim();
  if (!lowerCaseQuery) {
    return [];
  }

  const routeResults = router.options.routes
    .filter((route) => {
      if (!route.meta || route.meta.includeInSearch === false) {
        return false;
      }
      return (
        (route.meta.title && route.meta.title.toLowerCase().includes(lowerCaseQuery))
        || (route.meta.description && route.meta.description.toLowerCase().includes(lowerCaseQuery))
      );
    })
    .map((route) => ({
      key: route.path,
      path: route.path,
      meta: route.meta,
    }));

  const pathsFromRoutes = new Set(
    routeResults.map((r) => (typeof r.path === 'string' ? r.path : '')).filter(Boolean)
  );

  const cmsPageResults = (publishedPages || [])
    .filter((page) => page && page.slug)
    .map((page) => {
      const title = String(page.title || '').toLowerCase();
      const metaTitle = String(page.metaTitle || '').toLowerCase();
      const metaDescription = String(page.metaDescription || '');
      const metaKeywords = String(page.metaKeywords || '');
      const metaDescLower = metaDescription.toLowerCase();
      const metaKwLower = metaKeywords.toLowerCase();
      const { plain: blocksPlain, lower: blocksLower } = getCachedBlocksPlain(page);

      const matchedMeta = (
        title.includes(lowerCaseQuery)
        || metaTitle.includes(lowerCaseQuery)
        || metaDescLower.includes(lowerCaseQuery)
        || metaKwLower.includes(lowerCaseQuery)
      );
      const matchedBlocks = blocksLower.includes(lowerCaseQuery);
      if (!matchedMeta && !matchedBlocks) {
        return null;
      }

      let description = metaDescription || metaKeywords || '';
      if (matchedBlocks && (!metaDescLower.includes(lowerCaseQuery))) {
        const excerpt = makeExcerpt(blocksPlain, lowerCaseQuery);
        if (excerpt) {
          description = excerpt;
        } else if (!description && blocksPlain) {
          description = blocksPlain.slice(0, 120);
        }
      }

      const slugPath = `/${String(page.slug).replace(/^\/+|\/+$/g, '')}`;
      return {
        key: `cms-page-${page.id || page.slug}`,
        path: slugPath,
        meta: {
          title: page.title || page.metaTitle || 'Страница',
          description,
        },
      };
    })
    .filter((item) => item && item.path.length > 1 && !pathsFromRoutes.has(item.path));

  const paidServicesResults = (paidServices || [])
    .flatMap((group) => (group.services || [])
      .filter((service) => {
        const serviceName = String(service.name || '').toLowerCase();
        const serviceId = String(service.id || '').toLowerCase();
        const servicePrice = String(service.price || '').toLowerCase();
        const groupName = String(group.groupName || '').toLowerCase();
        return (
          serviceName.includes(lowerCaseQuery)
          || serviceId.includes(lowerCaseQuery)
          || servicePrice.includes(lowerCaseQuery)
          || groupName.includes(lowerCaseQuery)
        );
      })
      .map((service) => ({
        key: `paid-${group.groupName}-${service.id}-${service.name}`,
        path: {
          path: '/paid-services',
          query: {
            ...(service.id != null && String(service.id).trim()
              ? { serviceId: String(service.id).trim() }
              : {}),
            ...(group.groupName ? { group: String(group.groupName) } : {}),
          },
        },
        meta: {
          title: service.name || 'Платная услуга',
          description: `${group.groupName || 'Платные услуги'}${service.id ? `, ID: ${service.id}` : ''}${service.price ? `, ${service.price} руб.` : ''}`,
        },
      })));

  const newsResults = (news || [])
    .filter((item) => {
      const title = String(item.title || '').toLowerCase();
      const description = String(item.description || '').toLowerCase();
      return title.includes(lowerCaseQuery) || description.includes(lowerCaseQuery);
    })
    .map((item) => ({
      key: `news-${item.id}`,
      path: `/about/news/${item.id}`,
      meta: {
        title: item.title || 'Новость',
        description: item.description || 'Новости центра',
      },
    }));

  const allDoctors = [
    ...(employees || []),
    ...(managements || []),
    ...(chiefMedicalOfficer || []),
  ];

  const specialistsResults = allDoctors
    .filter((doctor) => {
      const name = String(doctor.name || '').toLowerCase();
      const position = String(doctor.position || '').toLowerCase();
      const department = String(doctor.mainDepartment || '').toLowerCase();
      const opening = String(doctor.opening || '').toLowerCase();
      return (
        name.includes(lowerCaseQuery)
        || position.includes(lowerCaseQuery)
        || department.includes(lowerCaseQuery)
        || opening.includes(lowerCaseQuery)
      );
    })
    .map((doctor) => ({
      key: `doctor-${doctor.id}-${doctor.name}`,
      path: {
        path: '/about/specialists',
        query: {
          specialist: doctor.name || '',
          ...(doctor.id != null ? { specialistId: String(doctor.id) } : {}),
        },
      },
      meta: {
        title: doctor.name || 'Специалист',
        description: `${doctor.position || 'Специалист'}${doctor.mainDepartment ? `, ${doctor.mainDepartment}` : ''}`,
      },
    }));

  return [
    ...routeResults,
    ...cmsPageResults,
    ...paidServicesResults,
    ...newsResults,
    ...specialistsResults,
  ];
}
